import { useState, useEffect, useMemo } from 'react';
import { getApiClient } from 'utils/apiClient';
import logger from 'utils/logger';
import { getSwitcherEmailDisplay, getAccountMongoLookupOid } from 'utils/msalAccountDisplay';

/**
 * Mongo-backed display for account switcher: by email and by Entra oid.
 *
 * @param {import('@azure/msal-browser').AccountInfo[]} allCachedAccounts
 * @param {Array<Record<string, unknown>>} historicalAccounts
 * @param {import('@azure/msal-browser').PublicClientApplication|null} instance
 * @returns {{ byEmail: Record<string, { name: string, email: string }>, byOid: Record<string, { name: string, email: string }> }}
 */
export function useMongoAccountDisplayByEmailMap(allCachedAccounts, historicalAccounts, instance) {
  const [byEmail, setByEmail] = useState({});
  const [byOid, setByOid] = useState({});

  const signature = useMemo(() => {
    const a = (allCachedAccounts || []).map((x) => x.homeAccountId).join(',');
    const h = (historicalAccounts || []).map((x) => x.homeAccountId).join(',');
    return `${a}|${h}`;
  }, [allCachedAccounts, historicalAccounts]);

  useEffect(() => {
    if (!instance) {
      setByEmail({});
      setByOid({});
      return;
    }
    const emails = new Set();
    const oids = new Set();
    for (const acc of allCachedAccounts || []) {
      const e = getSwitcherEmailDisplay(acc);
      if (e) emails.add(e);
      const oid = getAccountMongoLookupOid(acc);
      if (oid) oids.add(oid);
    }
    for (const acc of historicalAccounts || []) {
      const e = getSwitcherEmailDisplay(acc);
      if (e) emails.add(e);
      const oid = getAccountMongoLookupOid(acc);
      if (oid) oids.add(oid);
    }
    const emailList = [...emails].slice(0, 24);
    const oidList = [...oids].slice(0, 24);
    if (emailList.length === 0 && oidList.length === 0) {
      setByEmail({});
      setByOid({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const client = getApiClient();
        const [emailRes, oidRes] = await Promise.all([
          emailList.length > 0
            ? client.resolveUsersDisplayByEmails(emailList)
            : Promise.resolve({ success: true, by_email: {} }),
          oidList.length > 0
            ? client.resolveUsersDisplayByOids(oidList)
            : Promise.resolve({ success: true, by_oid: {} }),
        ]);
        if (cancelled) return;
        if (emailRes && emailRes.success && emailRes.by_email && typeof emailRes.by_email === 'object') {
          setByEmail(emailRes.by_email);
        } else {
          setByEmail({});
        }
        if (oidRes && oidRes.success && oidRes.by_oid && typeof oidRes.by_oid === 'object') {
          setByOid(oidRes.by_oid);
        } else {
          setByOid({});
        }
      } catch (e) {
        logger.debug('ACCOUNT-SWITCHER', 'resolveUsersDisplay (email/oid) failed', { error: e?.message });
        if (!cancelled) {
          setByEmail({});
          setByOid({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance, signature]);

  return { byEmail, byOid };
}
